import { generatePath, useNavigate, useSearchParams } from "react-router-dom";
import { RoutePath } from "../routerPaths";
import { CrmLoadError } from "./components/CrmLoadError";
import { RecordFormPageLayout, RecordFormSkeleton, type Breadcrumb } from "./form/components/RecordFormPageLayout";
import { RecordForm } from "./form/RecordForm";
import { emptyRecordFormValues } from "./form/recordFormValues";
import { useRecordCreator } from "./form/useRecordCreator";

/** Query params: `group` locks the Record Group; `owner` is the record the new one should belong to. */
export function CrmCreateRecordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetGroupId = searchParams.get("group");
  const presetOwnerId = searchParams.get("owner");
  const groupPath = presetGroupId ? generatePath(RoutePath.CrmGroup, { masterId: presetGroupId }) : null;
  const creator = useRecordCreator({
    presetGroupId,
    onCreated: (result) => navigate(generatePath(RoutePath.CrmRecord, { recordId: result.record.id })),
  });

  if (creator.status === "error") {
    return <CrmLoadError subject={creator.subject} isRetrying={creator.isRetrying} onRetry={creator.retry} />;
  }

  const group = creator.status === "ready" ? creator.group : undefined;
  const breadcrumbs: Breadcrumb[] = [
    { label: "CRM", to: RoutePath.CrmList },
    ...(groupPath && group ? [{ label: group.long_name, to: groupPath }] : []),
    { label: "New record" },
  ];

  return (
    <RecordFormPageLayout breadcrumbs={breadcrumbs} title="New record">
      {creator.status === "loading" ? (
        <RecordFormSkeleton />
      ) : (
        <RecordForm
          mode="create"
          rules={creator.rules}
          defaultValues={emptyRecordFormValues(presetGroupId ?? "")}
          groupRecords={group?.records ?? []}
          groupName={group?.long_name ?? null}
          lockedFields={{ master_id: presetGroupId !== null }}
          presetOwnerId={presetOwnerId}
          onGroupChange={creator.onGroupChange}
          onSubmit={creator.onSubmit}
          onCancel={() => navigate(groupPath ?? RoutePath.CrmList)}
        />
      )}
    </RecordFormPageLayout>
  );
}
